import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { api, resolveImage } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Customers from "@/pages/Customers";
import Pricing from "@/pages/Pricing";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import NewOrder from "@/pages/NewOrder";
import Payments from "@/pages/Payments";
import Ledger from "@/pages/Ledger";
import Reports from "@/pages/Reports";
import MarketingPage from "@/pages/MarketingPage";
import Audit from "@/pages/Audit";
import Inventory from "@/pages/Inventory";
import InventoryProduct from "@/pages/InventoryProduct";
import Drivers from "@/pages/Drivers";
import RoutesPage from "@/pages/RoutesPage";
import Deliveries from "@/pages/Deliveries";
import Settings from "@/pages/Settings";
import Analytics from "@/pages/Analytics";
import DriverDashboard from "@/pages/DriverDashboard";
import DriverDeliveries from "@/pages/DriverDeliveries";
import DriverDeliveryDetail from "@/pages/DriverDeliveryDetail";
import "@/App.css";

function DynamicBranding() {
  useEffect(() => {
    api.get("/whitelabel")
      .then((res) => {
        const data = res.data;
        if (data.company_name) {
          document.title = data.meta_title || data.company_name;
        }
        if (data.logo_url) {
          const fullLogoUrl = resolveImage(data.logo_url);
          
          // Remove pre-existing default React favicons
          document.querySelectorAll("link[rel*='icon']").forEach(el => el.remove());
          
          // Create new brand favicon link tag
          const link = document.createElement("link");
          link.type = "image/x-icon";
          link.rel = "shortcut icon";
          link.href = fullLogoUrl;
          document.getElementsByTagName("head")[0].appendChild(link);

          const appleLink = document.createElement("link");
          appleLink.rel = "apple-touch-icon";
          appleLink.href = fullLogoUrl;
          document.getElementsByTagName("head")[0].appendChild(appleLink);
        }
      })
      .catch(() => {});
  }, []);

  return null;
}

function Protected({ children, allow, permKey }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) {
    return <Navigate to={user.role === "driver" ? "/driver" : "/dashboard"} replace />;
  }
  // Granular admin permissions check
  if (user.role === "admin" && !user.is_super_admin && permKey && user.permissions) {
    if (user.permissions[permKey] === false) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <DynamicBranding />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route element={<Protected><AppShell /></Protected>}>
            {/* Admin + Customer */}
            <Route path="/dashboard" element={<Protected allow={["admin","customer"]}><Dashboard /></Protected>} />
            <Route path="/products" element={<Protected allow={["admin"]} permKey="products"><Products /></Protected>} />
            <Route path="/customers" element={<Protected allow={["admin"]} permKey="customers"><Customers /></Protected>} />
            <Route path="/pricing" element={<Protected allow={["admin"]} permKey="pricing"><Pricing /></Protected>} />
            <Route path="/orders" element={<Protected allow={["admin","customer"]} permKey="orders"><Orders /></Protected>} />
            <Route path="/orders/new" element={<Protected allow={["admin"]} permKey="orders"><NewOrder /></Protected>} />
            <Route path="/orders/:id" element={<Protected allow={["admin","customer"]} permKey="orders"><OrderDetail /></Protected>} />
            <Route path="/payments" element={<Protected allow={["admin","customer"]} permKey="payments"><Payments /></Protected>} />
            <Route path="/ledger" element={<Protected allow={["admin","customer"]} permKey="ledger"><Ledger /></Protected>} />
            <Route path="/ledger/:cid" element={<Protected allow={["admin"]} permKey="ledger"><Ledger /></Protected>} />
            <Route path="/reports" element={<Protected allow={["admin"]} permKey="reports"><Reports /></Protected>} />
            <Route path="/marketing" element={<Protected allow={["admin"]} permKey="marketing"><MarketingPage /></Protected>} />
            <Route path="/audit" element={<Protected allow={["admin"]} permKey="audit"><Audit /></Protected>} />
            <Route path="/inventory" element={<Protected allow={["admin"]} permKey="inventory"><Inventory /></Protected>} />
            <Route path="/inventory/product/:pid" element={<Protected allow={["admin"]} permKey="inventory"><InventoryProduct /></Protected>} />
            <Route path="/drivers" element={<Protected allow={["admin"]} permKey="drivers"><Drivers /></Protected>} />
            <Route path="/routes" element={<Protected allow={["admin"]} permKey="routes"><RoutesPage /></Protected>} />
            <Route path="/deliveries" element={<Protected allow={["admin"]} permKey="deliveries"><Deliveries /></Protected>} />
            <Route path="/analytics" element={<Protected allow={["admin"]} permKey="analytics"><Analytics /></Protected>} />
            <Route path="/settings" element={<Protected allow={["admin"]} permKey="settings"><Settings /></Protected>} />
            {/* Driver */}
            <Route path="/driver" element={<Protected allow={["driver"]}><DriverDashboard /></Protected>} />
            <Route path="/driver/deliveries" element={<Protected allow={["driver"]}><DriverDeliveries /></Protected>} />
            <Route path="/driver/deliveries/:did" element={<Protected allow={["driver"]}><DriverDeliveryDetail /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
